import React, { useEffect, useMemo } from "react";
import { Button, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import MainLayout from "../../shared/layouts/main.layout";
import { ROUTE_LOGIN } from "../../shared/routes/auth.routes";

import {
  ROUTES_OPEN,
  ROUTES_PROTECTED
} from "../../shared/routes/renderer.route";
import { RootState } from "../../shared/types/common";
import { setActiveRoute } from "../store/reducers/settings.slice";
import { ROUTE_DASHBOARD } from "../../shared/routes/private.routes";
import { getMyPermissions } from "../store/reducers/auth.slice";

function RouteGuard() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { accessToken, myPermissions, permissionsStatus } = useSelector((state: RootState) => state.auth);
  const accessibleProtectedRoutes = useMemo(() => ROUTES_PROTECTED, []);

  useEffect(() => {
    if (accessToken) {
      dispatch(getMyPermissions()).then((result: any) => {
        if (getMyPermissions.fulfilled.match(result)) {
          window.dispatchEvent(new Event("rp:checkRelease"));
        }
      });
    }
  }, [accessToken, dispatch]);

  useEffect(() => {
    const activeRoute = [...accessibleProtectedRoutes]
      .sort((a, b) => b.route.length - a.route.length)
      .find((route) => {
        const routePath = route.route.replace(/\/:[^/]+/g, "");
        return (
          location.pathname === routePath ||
          location.pathname.startsWith(`${routePath}/`)
        );
      });
  
    if (activeRoute) {
      const { name, route: path } = activeRoute;
      dispatch(setActiveRoute({ name, path })); // ✅ Only dispatch serializable values
    }
  }, [location, dispatch, accessibleProtectedRoutes]);
  
  
  const userPermissionSet = new Set<string>(
  (myPermissions ?? []).map((p: any) => p.permission.name)
);

const checkPermissions = (
  userPermissions: Set<string>,
  permissions: string[]
) => {
  return permissions.some((permission) =>
    userPermissions.has(permission)
  );
};

const findFirstAccessibleRoute = (
  routes: any[],
  userPermissions: Set<string>
): string | null => {
  for (const route of routes) {
    if (
      route.permission &&
      checkPermissions(userPermissions, route.permission)
    ) {
      return route.route;
    }
  }

  return null;
};

const defaultRoute = accessToken
 // userPermissionSet.has("dashboard.view")
    ? ROUTE_DASHBOARD
    : findFirstAccessibleRoute(
        accessibleProtectedRoutes,
        userPermissionSet
      ) || "/unauthorized";

  // A token arrives before permissions. Keep the callback URL intact until
  // authorization has completed, including when restoring a persisted session.
  if (accessToken && permissionsStatus !== "succeeded") {
    return permissionsStatus === "failed" ? (
      <div role="alert">
        <p>Unable to load your permissions. Please try again.</p>
        <Button onClick={() => dispatch(getMyPermissions())}>Try again</Button>
      </div>
    ) : (
      <div role="status" aria-label="Loading permissions"><Spin /></div>
    );
  }

  return accessToken ? (
    <React.Fragment>
      <Routes>
        {accessibleProtectedRoutes.map((route: any, index: number) => {
          return (
            <Route
              path={route.route}
              key={index}
              element={
                ['/print-receipt/:id', '/print-online-receipt/:id'].indexOf(route.route) === -1 ?
                  <MainLayout>{route.component}</MainLayout>
                  : <>{route.component}</>
              }
            />
          );
        })}
        {/* <Route path="*" element={<Navigate to={ROUTE_DASHBOARD} replace />} /> */}
       <Route path="*" element={<Navigate to={defaultRoute} replace />} />
      </Routes>
    </React.Fragment>
  ) : (
    <Routes>
      {ROUTES_OPEN.map((route: any, index: number) => {
        return (
          <Route path={route.route} key={index} element={route.component} />
        );
      })}
      <Route path="*" element={<Navigate to={ROUTE_LOGIN} replace />} />
    </Routes>
  );
}

export default RouteGuard;
